export const COVER_PAGE_INLINE_EDITOR_SOFT_STYLES = {
  borderWidth: "1px",
  borderColor: "blackAlpha.100",
  borderRadius: "card",
  boxShadow: "none",
  bg: "transparent",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  "&:focus-within": {
    borderColor: "blackAlpha.400",
    boxShadow: "0 0 0 1px rgba(45, 55, 72, 0.14)"
  },
  "& .ProseMirror": {
    color: "PRIMARY_GRAY",
    lineHeight: 1.6,
    paddingInline: "16px",
    paddingBlock: "14px",
    borderRadius: "var(--chakra-radii-card)",
  },
  "& .ProseMirror p": {
    margin: "0 0 0.75em"
  },
  "& .ProseMirror p:last-of-type": {
    marginBottom: "0"
  }
} as const

export const COVER_PAGE_INLINE_EDITOR_SOFT_STYLES_SPACIOUS = {
  ...COVER_PAGE_INLINE_EDITOR_SOFT_STYLES,
  "& .ProseMirror": {
    ...COVER_PAGE_INLINE_EDITOR_SOFT_STYLES["& .ProseMirror"],
    paddingInline: "18px",
    paddingBlock: "16px",
  }
} as const

// Editing in place. The field takes over the Placeholder it replaces: the
// same soft fill, radius, and padding, so the text lands where the label was.
// The field holds the editor and, in its footer, the actions.
export const COVER_PAGE_INLINE_EDITOR_FIELD_STYLES = {
  // The 1px border stays (transparent) so nothing shifts against view mode.
  borderWidth: "1px",
  borderColor: "transparent",
  borderRadius: "card",
  bg: "blackAlpha.50",
  transition: "box-shadow 0.2s ease",
  "&:focus-within": {
    boxShadow: "0 0 0 1px rgba(45, 55, 72, 0.14)"
  }
} as const

// The editor inside that field: no frame of its own.
export const COVER_PAGE_INLINE_EDITOR_EDITING_STYLES = {
  ...COVER_PAGE_INLINE_EDITOR_SOFT_STYLES,
  borderWidth: 0,
  bg: "transparent",
  "&:focus-within": { boxShadow: "none" },
  "& .ProseMirror": {
    ...COVER_PAGE_INLINE_EDITOR_SOFT_STYLES["& .ProseMirror"],
    paddingInline: "15px",
    paddingTop: "15px",
    paddingBottom: "8px",
    minHeight: "5em"
  },
  // The shared editor hides its placeholder on focus. This one opens focused,
  // so keep the prompt visible until the first keystroke.
  "& .ProseMirror.ProseMirror-focused p.is-editor-empty:first-of-type::before":
    {
      display: "block",
      float: "left",
      height: 0
    }
} as const

/**
 * Hides an editing control until its container is hovered or focused, so a
 * finished page looks finished. `revealSelector` is the full selector that
 * shows it, ending in `&` (e.g. `[role='group']:hover > &`).
 *
 * With no hover (touch screens) the control is always shown: there is nothing
 * to hover, and on a phone the design panel covers the page, so a "show while
 * the panel is open" rule would never be seen.
 */
export const coverPageRevealOnHoverCss = (revealSelector: string) => ({
  opacity: 0,
  transition: "opacity 0.2s",
  [revealSelector]: { opacity: 1 },
  "@media (hover: none)": { opacity: 1 }
})

/** Reveal for a control anywhere inside a section (`role="group"`). */
export const COVER_PAGE_REVEAL_IN_SECTION =
  "[role='group']:hover &, [role='group']:focus-within &"
